import styled from '@emotion/styled';
import { Else, If, Then, When } from 'react-if';

import {
    NeighborhoodStatus,
    useMapContext,
    usePostCode,
    useScrollContext,
} from '../../hooks';
import { AnalyticsTargetLayerAreaProvider, FadeWrapper, mqUp } from 'rwl-ui';
import { NeighborhoodOverviewBanner } from '../NeighborhoodOverviewBanner/NeighborhoodOverviewBanner';
import { MediaDetailsModal, DetailsView } from '../../components';
import { FeaturedContentLists } from './FeaturedContentLists';
import { useEffect, useRef, useState } from 'react';
import { useContentDetails } from '../../providers';
import { TargetLayerArea } from 'rwl-shared/dist/models/analytics';

const Overview = styled.div<{ isExpanded: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    ${mqUp('sm')} {
        display: unset;
        flex: unset;
        transition: flex 0.3s ease; /* Smooth transition */
        transition: display 0.3s ease; /* Smooth transition */
        overflow: auto;
        padding-right: var(--rwl-spacing-sm);
        width: ${({ isExpanded }) => (isExpanded ? '66.67%' : '50%')};
        transition: width 0.3s ease; /* Smooth transition */
    }
`;

export const FeaturedContent = () => {
    const { isDetailsOpen } = useContentDetails();
    const postCode = usePostCode();
    const { neighborhoodStatus } = useMapContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleComponent, setVisibleComponent] = useState<
        'details' | 'default'
    >('default');
    const isExpanded = Boolean(
        isDetailsOpen ||
            (postCode && neighborhoodStatus === NeighborhoodStatus.FOUND)
    );

    useEffect(() => {
        if (isDetailsOpen) setVisibleComponent('details');
        else setVisibleComponent('default');
    }, [isDetailsOpen]);

    const { scrollPosition, setScrollPosition } = useScrollContext();

    const saveScrollPosition = () => {
        if (containerRef.current && !isDetailsOpen) {
            setScrollPosition(containerRef.current.scrollTop);
        }
    };

    useEffect(() => {
        const container = containerRef.current;

        // Restore scroll position on mount
        if (container && !isDetailsOpen) {
            container.scrollTop = scrollPosition;
        }
    }, [isDetailsOpen]);

    return (
        <Overview
            onScroll={saveScrollPosition}
            ref={containerRef}
            isExpanded={isExpanded}
        >
            <If condition={isDetailsOpen}>
                <Then>
                    <FadeWrapper isVisible={visibleComponent === 'details'}>
                        <DetailsView containerRef={containerRef} />
                    </FadeWrapper>
                </Then>
                <Else>
                    <FadeWrapper isVisible={!!postCode}>
                        <When condition={!!postCode}>
                            <NeighborhoodOverviewBanner />
                        </When>
                    </FadeWrapper>
                    <FadeWrapper isVisible={visibleComponent === 'default'}>
                        <FeaturedContentLists />
                    </FadeWrapper>
                </Else>
            </If>
            <AnalyticsTargetLayerAreaProvider
                targetLayerArea={TargetLayerArea.MEDIA}
            >
                <MediaDetailsModal />
            </AnalyticsTargetLayerAreaProvider>
        </Overview>
    );
};
